import { z } from "zod";
import type { LocaleType } from "@/constants/locales";
import { buildDiscoverUrl, isPlaceName } from "./urlBuilders";
import type { CustomLocation } from "@/utils/location";

/** A plain decimal within ±limit, as the app writes it: "59.929", "-0.128". */
const coordinate = (limit: number) =>
  z
    .string()
    .regex(/^-?\d+(\.\d+)?$/)
    .transform(Number)
    .pipe(z.number().min(-limit).max(limit));

const PointParams = z.object({
  lat: coordinate(90),
  lon: coordinate(180),
  // Anything but one name with a letter in it (none, repeated, "59.9, 10.7") is no name
  locationName: z
    .string()
    .transform((name) => name.trim().slice(0, 200))
    .refine(isPlaceName)
    .catch(""),
});

/**
 * The point in `?lat&lon&locationName`, or null unless `lat` and `lon` are
 * each one decimal in range. Takes Next.js search params, where a repeated
 * key is an array, or `URLSearchParams.get` results.
 */
export function parseCustomLocation(searchParams: {
  lat?: unknown;
  lon?: unknown;
  locationName?: unknown;
}): CustomLocation | null {
  const result = PointParams.safeParse(searchParams);
  if (!result.success) return null;
  const { lat, lon, locationName } = result.data;
  return { lat, lon, displayName: locationName };
}

/**
 * The discover URL for a place and activity with every search param kept.
 * Next.js passes a repeated key (`?tab=a&tab=b`) as an array.
 */
export function buildRetryHref(
  locale: LocaleType,
  locationSlug: string,
  activitySlug: string | undefined,
  searchParams: Record<string, string | string[] | undefined>,
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(searchParams)) {
    if (value == null) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        params.append(key, item);
      }
    } else params.append(key, value);
  }
  return buildDiscoverUrl({
    locale,
    locationSlug,
    activitySlug,
    query: params,
  });
}
